import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Sparkles, ShieldCheck, Lock, CheckCircle2, XCircle, Clock,
  CreditCard, Building2, ArrowRight, Loader2, ExternalLink, LifeBuoy,
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";
import api from "../../lib/api";
import useIsMobile from "../../hooks/useIsMobile";
import InstaxIframe from "../InstaxIframe";

/**
 * Xchangely Pay branded checkout — Option A implementation.
 *
 * We render a fully Xchangely-branded order summary on our domain using data
 * from our own DB (via /api/xpay/public/link/:id/details). The upstream
 * provider's order/KYC form is embedded as an iframe BELOW our summary so the
 * customer's eye goes to the Xchangely branding first. PCI card entry on
 * Banxa is unavoidable per industry rules, but everything up to that moment
 * stays under our brand.
 *
 * Status pill polls our backend (no upstream cost) so a settled payment
 * triggers the "Thank you" view immediately.
 */
const METHOD_LABELS = {
  card: "Credit / debit card", ideal: "iDEAL", bancontact: "Bancontact",
  "apple-pay": "Apple Pay", "google-pay": "Google Pay", sepa: "SEPA transfer",
  eps: "EPS",
};
const fmt = (n, cur) => {
  if (n == null) return "—";
  try { return new Intl.NumberFormat(undefined, { style: "currency", currency: cur || "EUR" }).format(n); }
  catch { return `${n} ${cur || ""}`; }
};

export default function CheckoutFrame() {
  const { sessionId } = useParams();
  const isMobile = useIsMobile();   // Iter45 #21 — drives the handoff mode
  const [details, setDetails] = useState(null);
  const [detailsError, setDetailsError] = useState(false);
  const [status, setStatus] = useState(null);
  // Once the customer clicks the primary CTA we record that they've launched
  // the payment so we can show a "waiting for payment" state with a re-open
  // button if they accidentally close the new tab.
  const [launched, setLaunched] = useState(false);
  const [launchedAt, setLaunchedAt] = useState(0);
  // "Get help" panel state — opens when the customer can't proceed.
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpEmail, setHelpEmail] = useState("");
  const [helpMessage, setHelpMessage] = useState("");
  const [helpBusy, setHelpBusy] = useState(false);
  const [helpSent, setHelpSent] = useState(false);
  // 🔒 P0 audit finding #4 — use the REAL upstream URL the backend
  // stored when it created the session. The previous guess pattern
  // (`https://instaxchange.com/order/<sid>`) landed on dead pages for
  // any session whose upstream didn't follow that exact path; this is
  // the primary cause of the live "windows don't redirect" outage.
  // Empty string until /details responds — the CTA is disabled until then.
  const upstream = details?.provider_checkout_url || "";
  // 🩹 Iter49 — InstaXchange must be embedded in an iframe (their docs
  // explicitly require it since Sep 2025); the legacy new-tab flow hits
  // their deprecated code path and silently fails on `/init`. XGate SEPA
  // invoice URLs still use the new-tab launch since XGate isn't iframe-
  // capable. We detect the upstream by host fragment.
  const isInstax = !!upstream && /(^|\/\/)instaxchange\.com\//.test(upstream);

  // Stash the session id in localStorage so /pay/return can recover it
  // if the upstream's return redirect doesn't include a query param.
  useEffect(() => {
    if (!sessionId) return;
    try { localStorage.setItem("xch_last_session_id", sessionId); } catch (_e) { /* sandboxed-iframe / disabled storage — non-fatal */ }
  }, [sessionId]);

  // Load order details from our own backend.
  useEffect(() => {
    let stop = false;
    api.get(`/xpay/public/link/${sessionId}/details`)
      .then((r) => { if (!stop) { setDetails(r.data); setStatus(r.data?.status || null); } })
      .catch(() => { if (!stop) setDetailsError(true); });
    return () => { stop = true; };
  }, [sessionId]);

  // Poll status every 6s on our own /status endpoint.
  useEffect(() => {
    let stop = false;
    const tick = async () => {
      try {
        const r = await api.get(`/xpay/public/link/${sessionId}/status`);
        if (!stop) setStatus(r.data?.status || null);
      } catch (_e) { /* swallow */ }
    };
    const iv = setInterval(tick, 6000);
    return () => { stop = true; clearInterval(iv); };
  }, [sessionId]);

  // XGate SEPA invoices still use the new-tab launch flow (their hosted
  // invoice page isn't iframe-friendly). Detection via popup-block null
  // check — we strip `opener` post-hoc so the security posture matches
  // noopener without misfiring the block detector (which would same-tab
  // navigate away and lose status polling + help fallback).
  const [popupBlocked, setPopupBlocked] = useState(false);
  const launchPayment = () => {
    if (!upstream) return;
    setLaunched(true);
    setLaunchedAt(Date.now());
    // 🔒 Iter45 #21 — Mobile handoff strategy. window.open(_, "_blank") is
    // routinely blocked or mangled by mobile browsers (iOS Safari pops a
    // confirmation, Android Chrome opens a hidden tab the user can't find).
    // Mobile = the dominant payment surface for B2B checkout, so we MUST
    // not gamble. Detection via `useIsMobile()` → top-level same-tab nav
    // on mobile. Session id was already stashed in localStorage on mount
    // so /pay/return can recover the customer's branded experience
    // post-payment. Desktop keeps the existing new-tab flow so the user
    // retains the order summary + status polling + help fallback in the
    // original tab.
    if (isMobile) {
      window.location.href = upstream;
      return;
    }
    const win = window.open(upstream, "_blank");
    if (!win) {
      // Genuine desktop popup-block — render inline anchor (next click =
      // real user gesture). Do NOT same-tab-nav on desktop; the user
      // would lose the order summary + status polling + help fallback.
      setPopupBlocked(true);
      toast.error("Your browser blocked the popup. Tap the link below to continue.");
    } else {
      try { win.opener = null; } catch (_e) { /* same-origin only */ }
      setPopupBlocked(false);
    }
  };

  // Auto-suggest the help panel after ~90 seconds with no settled webhook.
  // We don't open it (would be intrusive); we surface a small "I'm stuck"
  // CTA prominently so the customer is never abandoned in the wait.
  // A 15s ticker keeps `stuckSuggested` reactive without calling Date.now()
  // during render (eslint react-hooks/purity).
  const [tickNow, setTickNow] = useState(() => Date.now());
  useEffect(() => {
    if (!launched) return undefined;
    const iv = setInterval(() => setTickNow(Date.now()), 15_000);
    return () => clearInterval(iv);
  }, [launched]);
  const stuckSuggested = launched && launchedAt > 0
    && (tickNow - launchedAt) > 90_000
    && !status?.toLowerCase()?.match(/completed|settled|paid|failed|cancelled/);

  // Submit the help form: POST captures customer contact + message and the
  // backend fires emails to the merchant + ops + flags the link in admin.
  const submitHelp = async () => {
    if (!helpEmail.trim()) {
      toast.error("Please enter the email we can reach you on.");
      return;
    }
    setHelpBusy(true);
    try {
      await api.post(`/xpay/public/link/${sessionId}/help`, {
        contact_email: helpEmail.trim(),
        message:       helpMessage.trim() || null,
      });
      setHelpSent(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send help request — try again.");
    } finally { setHelpBusy(false); }
  };

  const settled  = status && ["completed", "settled", "paid"].includes(status.toLowerCase());
  const failed   = status && ["failed", "expired", "rejected", "cancelled"].includes(status.toLowerCase());
  // 🩹 Iter51 — intermediate state. Once the backend has advanced past
  // `initiated` (e.g., InstaXchange's webhook has flipped us to `pending`
  // or `processing`), the InstaXchange iframe is stale — it shows their
  // "Payment Session Is Already In Progress" error to the customer who
  // just successfully paid. We hide the iframe and render a native
  // "Payment is processing" panel so the customer sees calm, branded
  // reassurance until the settled webhook lands. The final success
  // state below still takes over once status flips to completed/paid.
  const processing = status
    && ["pending", "processing"].includes(status.toLowerCase())
    && !settled && !failed;

  // ===== Settled / failed final states (full-page takeover) =====
  if (settled) {
    return <FinalState
      tone="ok" icon={CheckCircle2}
      title="Payment received"
      body="Thank you. The merchant has been notified and your funds are on the way. You can close this window."
    />;
  }
  if (failed) {
    return <FinalState
      tone="bad" icon={XCircle}
      title="Payment didn't complete"
      body="Contact the merchant for a new link. If you were charged, you'll receive a refund within 5 business days."
    />;
  }

  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="checkout-frame">
      {/* Brand header */}
      <header className="bg-foreground text-white" data-testid="checkout-header">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="w-8 h-8 rounded-xl bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-emerald-300" />
            </span>
            <div className="leading-tight">
              <div className="font-display font-bold text-base">Xchangely <span className="text-emerald-300">Pay</span></div>
              <div className="text-[10px] text-white/50">Secure crypto checkout</div>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-1.5 text-[10px] text-white/60">
            <Lock className="w-3 h-3 text-emerald-300" /> Encrypted end-to-end
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto w-full px-4 sm:px-5 py-5 sm:py-6 flex-1">
        {/* Branded order summary */}
        <section className="xch-card overflow-hidden mb-4" data-testid="checkout-order-summary">
          {details && (
            <div className="bg-gradient-to-br from-emerald-50/60 to-background border-b border-border px-5 sm:px-6 py-4">
              <div className="flex items-center gap-3 mb-1.5">
                {details.merchant_logo_url ? (
                  <img src={details.merchant_logo_url}
                        alt={details.merchant_name || "Merchant logo"}
                        className="h-9 max-w-[120px] object-contain"
                        data-testid="checkout-merchant-logo"
                        // Hide the broken-image affordance if the merchant's
                        // CDN serves a 404 / wrong MIME — graceful fallback
                        // to the existing text-only header.
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                ) : (
                  <Building2 className="w-3 h-3 text-muted-foreground" />
                )}
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold" data-testid="checkout-merchant-name">
                  Paying {details.merchant_name}
                </span>
              </div>
              <div className="flex items-baseline gap-3 flex-wrap">
                <span className="font-display font-bold text-2xl sm:text-3xl text-foreground" data-testid="checkout-amount">
                  {fmt(details.amount, details.from_currency)}
                </span>
                {details.to_currency && (
                  <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                    <ArrowRight className="w-3 h-3" />
                    settled in {details.to_currency}
                  </span>
                )}
              </div>
              {details.reference && (
                <div className="text-[11px] text-muted-foreground mt-2" data-testid="checkout-reference">
                  Order reference: <span className="font-mono text-foreground">{details.reference}</span>
                </div>
              )}
            </div>
          )}
          {detailsError && (
            <div className="px-5 sm:px-6 py-4 text-xs text-muted-foreground" data-testid="checkout-details-error">
              We couldn&apos;t load the order details, but you can still complete the payment below.
            </div>
          )}
          {!details && !detailsError && (
            <div className="px-5 sm:px-6 py-6 flex items-center justify-center" data-testid="checkout-details-loading">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          )}

          {/* Trust strip */}
          <div className="px-5 sm:px-6 py-2.5 grid grid-cols-3 gap-3 text-[10px] text-muted-foreground border-t border-border/50">
            <TrustStrip icon={ShieldCheck} text="PCI-compliant" />
            <TrustStrip icon={Lock} text="Encrypted in transit" />
            <TrustStrip icon={CreditCard} text={METHOD_LABELS[details?.method] || "Card payment"} />
          </div>
        </section>

        {/* PRIMARY ACTION —
            • status=pending/processing: hide the upstream completely
              (InstaXchange's iframe goes stale and shows "session already
              in progress" once the webhook has fired). Render a native
              "Payment is processing" panel instead.
            • InstaXchange (card/iDEAL/PayPal/Bancontact/etc.) when still
              `initiated`: embed their hosted checkout in an iframe inline
              below the order summary. Their docs since Sep 2025 require
              iframe mode; the new-tab flow silently fails on `/init`.
            • XGate SEPA invoice: keep the new-tab launch — XGate's
              invoice page isn't iframe-friendly. */}
        {processing ? (
          <ProcessingPanel
            amount={details?.amount}
            currency={details?.from_currency}
            reference={details?.reference}
          />
        ) : isInstax ? (
          <section className="xch-card overflow-hidden mb-4" data-testid="checkout-instax-host">
            <div className="px-5 sm:px-6 py-3 border-b border-border/50 flex items-center gap-2">
              <Lock className="w-4 h-4 text-emerald-700" />
              <div className="text-xs font-display font-bold">Complete your payment securely</div>
              <div className="ml-auto text-[10px] text-muted-foreground">PCI-DSS Level 1</div>
            </div>
            <div className="bg-slate-50" style={{ minHeight: 760 }}>
              <InstaxIframe url={upstream} testid="checkout-instax-iframe" />
            </div>
          </section>
        ) : (
        <section className="xch-card p-5 sm:p-6 mb-4" data-testid="checkout-primary-cta">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-2xl bg-emerald-50 border border-emerald-200 flex items-center justify-center shrink-0">
              <Lock className="w-4 h-4 text-emerald-700" />
            </div>
            <div className="min-w-0">
              <h2 className="font-display font-bold text-base leading-tight">
                Complete your payment securely
              </h2>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                We hand you off to our PCI-DSS Level 1 certified payment partner
                in a new tab. {launched
                  ? "Your order keeps polling here — once you finish, this page updates automatically. You can close the payment tab once you see the success screen."
                  : "Your card details never touch Xchangely Pay. Come back to this page to see the receipt — we'll update it for you."}
              </p>
            </div>
          </div>
          <Button onClick={launchPayment}
                  disabled={!upstream}
                  className="w-full h-12 rounded-xl gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-base font-display font-bold disabled:opacity-60 disabled:cursor-wait"
                  data-testid="checkout-launch-btn">
            {upstream ? (
              <>
                <ExternalLink className="w-4 h-4" />
                {launched
                  ? (isMobile ? "Continue to payment" : "Re-open payment tab")
                  : "Continue to secure payment"}
              </>
            ) : (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Preparing secure payment…
              </>
            )}
          </Button>
          {popupBlocked && (
            <div className="mt-3 rounded-xl border border-amber-300 bg-amber-50 px-3.5 py-2.5 text-[12px] text-amber-900"
                  data-testid="checkout-popup-blocked">
              <div className="font-bold mb-1">Popup blocked by your browser.</div>
              <a href={upstream} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 underline font-semibold"
                  data-testid="checkout-popup-blocked-link">
                <ExternalLink className="w-3.5 h-3.5" /> Tap here to open the payment page
              </a>
              <div className="text-[11px] text-amber-800/85 mt-1">
                We keep this tab updated as soon as your payment lands — don&apos;t close it.
              </div>
            </div>
          )}
          {launched && (
            <div className="mt-3 flex items-center justify-center gap-2 text-[11px] text-muted-foreground"
                 data-testid="checkout-waiting">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              <span>Waiting for payment confirmation… this page will update automatically.</span>
            </div>
          )}
        </section>
        )}

        {/* Get-help panel — always reachable; auto-suggested after ~90s of no
            settled webhook. The point: NO customer can ever get stranded.
            One click → backend captures contact + notifies merchant + ops. */}
        {!helpSent ? (
          <section className={`xch-card mt-4 ${stuckSuggested ? "border-amber-300 bg-amber-50" : ""}`}
                    data-testid="checkout-help-panel">
            {!helpOpen ? (
              <div className="p-4 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-start gap-2.5 min-w-0">
                  <LifeBuoy className={`w-4 h-4 mt-0.5 shrink-0 ${stuckSuggested ? "text-amber-700" : "text-muted-foreground"}`} />
                  <div className="text-xs leading-snug">
                    {stuckSuggested ? (
                      <>
                        <strong className="text-amber-900">Taking too long?</strong>{" "}
                        <span className="text-amber-900/85">
                          We&apos;ll get our team to reach out to you and process
                          your order manually so you&apos;re never stuck.
                        </span>
                      </>
                    ) : (
                      <span className="text-muted-foreground">
                        Trouble completing payment? Reach our team — we&apos;ll process your order manually.
                      </span>
                    )}
                  </div>
                </div>
                <Button onClick={() => setHelpOpen(true)} size="sm"
                        variant={stuckSuggested ? "default" : "outline"}
                        className={`rounded-xl gap-1.5 ${stuckSuggested ? "bg-amber-600 hover:bg-amber-700 text-white" : ""}`}
                        data-testid="checkout-help-open">
                  <LifeBuoy className="w-3.5 h-3.5" /> I&apos;m stuck — get help
                </Button>
              </div>
            ) : (
              <div className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <LifeBuoy className="w-4 h-4 text-amber-700" />
                  <div className="font-display font-bold text-sm">Get help with your order</div>
                </div>
                <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
                  Drop your email below — our team and the merchant will be
                  notified instantly. We can process your payment manually
                  (SEPA bank transfer or a fresh link) within one business day.
                </p>
                <div className="space-y-3">
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Your email *</Label>
                    <Input type="email" value={helpEmail}
                            onChange={(e) => setHelpEmail(e.target.value)}
                            placeholder="you@example.com"
                            className="rounded-xl mt-1"
                            data-testid="checkout-help-email" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">What happened? (optional)</Label>
                    <Textarea value={helpMessage}
                              onChange={(e) => setHelpMessage(e.target.value)}
                              rows={3}
                              placeholder="Button stuck on spinner, page not loading, etc."
                              className="rounded-xl mt-1 resize-none"
                              data-testid="checkout-help-message" />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button variant="ghost" size="sm" onClick={() => setHelpOpen(false)}
                            disabled={helpBusy}
                            data-testid="checkout-help-cancel">Cancel</Button>
                    <Button onClick={submitHelp} disabled={helpBusy}
                            className="rounded-xl gap-1.5 bg-amber-600 hover:bg-amber-700 text-white"
                            data-testid="checkout-help-submit">
                      <LifeBuoy className="w-3.5 h-3.5" />
                      {helpBusy ? "Sending…" : "Send help request"}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </section>
        ) : (
          <section className="xch-card mt-4 p-4 border-emerald-300 bg-emerald-50"
                    data-testid="checkout-help-sent">
            <div className="flex items-start gap-2.5">
              <CheckCircle2 className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />
              <div className="text-xs text-emerald-900 leading-snug">
                <strong>Help request sent.</strong> Our team and the merchant
                have been notified at the same time. Expect to hear back at{" "}
                <strong>{helpEmail}</strong> within one business day. You can
                close this tab — your order is saved.
              </div>
            </div>
          </section>
        )}

        {/* Footnote */}
        <div className="mt-4 max-w-xl mx-auto" data-testid="checkout-info">
          <div className="text-[11px] text-muted-foreground text-center leading-relaxed">
            Card details are collected directly by our PCI-DSS Level 1 certified
            payment processor. Xchangely Pay never sees or stores your card
            number. Your funds settle to the merchant in stablecoin within minutes.
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-foreground border-t border-white/5 py-3 text-[10px] text-white/40" data-testid="checkout-footer">
        <div className="max-w-3xl mx-auto px-5 flex flex-wrap items-center justify-between gap-2">
          <div>© {new Date().getFullYear()} Xchangely Pay</div>
          <a href="mailto:support@xchangely.io" className="hover:text-white/70" data-testid="checkout-footer-support">
            Need help? support@xchangely.io
          </a>
        </div>
      </footer>
    </div>
  );
}


const TrustStrip = ({ icon: Icon, text }) => (
  <div className="flex items-center gap-1.5">
    <Icon className="w-3 h-3 text-emerald-700" />
    <span className="truncate">{text}</span>
  </div>
);


// Native "Payment is processing" panel — replaces the upstream iframe
// once status advances past `initiated`. Same `.xch-card` wrapper as the
// `initiated` host so the layout doesn't jump when the status poll lands.
//
// 🩹 Iter53 — copy correction. Previous version used premature-success
// language ("We've received your payment" + "you can safely close this
// tab") which caused real customers to close the browser BEFORE their
// card challenge resolved, then assume the payment went through. The
// pending state can also reflect a 3-D Secure / OTP step still pending
// on the issuer side — we must not declare success until the link
// genuinely reaches `completed`/`settled`/`paid`. This copy is neutral
// and explicitly asks the customer to STAY on the page.
function ProcessingPanel({ amount, currency, reference }) {
  return (
    <section className="xch-card overflow-hidden mb-4" data-testid="checkout-processing-panel">
      <div className="px-5 sm:px-6 py-3 border-b border-border/50 flex items-center gap-2">
        <Loader2 className="w-4 h-4 text-amber-600 animate-spin" />
        <div className="text-xs font-display font-bold">Payment is processing</div>
        <div className="ml-auto text-[10px] text-muted-foreground">Live update</div>
      </div>
      <div className="px-5 sm:px-6 py-8 sm:py-10 flex flex-col items-center text-center">
        <div className="w-16 h-16 rounded-2xl bg-amber-50 border border-amber-200 flex items-center justify-center mb-5">
          <Loader2 className="w-7 h-7 text-amber-600 animate-spin" />
        </div>
        <h2 className="font-display font-bold text-xl sm:text-2xl mb-2" data-testid="checkout-processing-title">
          Payment is processing
        </h2>
        <p className="text-sm text-muted-foreground max-w-sm leading-relaxed mb-2" data-testid="checkout-processing-body">
          Please don&apos;t close this window — your bank or card issuer is
          still confirming the transaction. This page will update
          automatically as soon as the payment is finalised.
        </p>
        <p className="text-xs text-amber-700 font-medium max-w-sm leading-relaxed mb-5">
          The transaction is <strong>not</strong> complete yet. Closing this
          page now may interrupt your payment.
        </p>
        {(amount != null || reference) && (
          <div className="rounded-xl border border-border bg-muted/30 px-4 py-3 text-left w-full max-w-sm" data-testid="checkout-processing-meta">
            {amount != null && (
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-muted-foreground">Amount</span>
                <span className="font-mono font-semibold text-foreground" data-testid="checkout-processing-amount">{fmt(amount, currency)}</span>
              </div>
            )}
            {reference && (
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Reference</span>
                <span className="font-mono text-foreground" data-testid="checkout-processing-reference">{reference}</span>
              </div>
            )}
          </div>
        )}
        <div className="mt-5 inline-flex items-center gap-2 text-[11px] text-muted-foreground">
          <Clock className="w-3.5 h-3.5" />
          <span>Most payments confirm within a few minutes.</span>
        </div>
      </div>
    </section>
  );
}


function FinalState({ tone, icon: Icon, title, body }) {
  const ring = tone === "ok"
    ? "bg-emerald-50 border-emerald-200 text-emerald-700"
    : "bg-rose-50 border-rose-200 text-rose-700";
  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid={`checkout-final-${tone}`}>
      <header className="bg-foreground text-white">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center gap-2.5">
          <span className="w-8 h-8 rounded-xl bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-emerald-300" />
          </span>
          <div className="font-display font-bold text-base">Xchangely <span className="text-emerald-300">Pay</span></div>
        </div>
      </header>
      <main className="flex-1 flex items-center justify-center px-5">
        <div className="max-w-md w-full xch-card p-8 text-center">
          <div className={`w-16 h-16 rounded-2xl mx-auto mb-5 flex items-center justify-center border ${ring}`}>
            <Icon className="w-7 h-7" />
          </div>
          <h1 className="font-display font-bold text-2xl mb-2">{title}</h1>
          <p className="text-sm text-muted-foreground leading-relaxed mb-6">{body}</p>
          <Button asChild variant="outline" className="rounded-xl gap-1.5">
            <a href="/" data-testid="checkout-final-back-home">Back to Xchangely.io</a>
          </Button>
        </div>
      </main>
    </div>
  );
}
